import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Descriptions, Form, Input, Modal, Select, Typography } from 'antd'
import type { BusinessOperation, BusinessStep } from '@/services/instanceWorkflows'
import type { EditResult } from '../contracts'
import type { WorkflowEdit } from '../config/workflow'

export type StepDraft = { kind: 'add' | 'update'; id: string; step?: BusinessStep }
const NAME_RULES = [{ required: true, whitespace: true, message: '请输入步骤名称' }]
const OPERATION_RULES = [{ required: true, message: '请选择此步骤要完成的业务操作' }]
const OK_PROPS = { 'aria-label': '确定步骤' }

export const StepForm = ({ draft, operations, disabled, onSubmit, onClose }: {
  draft: StepDraft; operations: BusinessOperation[]; disabled: boolean
  onSubmit: (command: Extract<WorkflowEdit, { kind: 'add' | 'update' }>) => EditResult; onClose: () => void
}) => {
  const [form] = Form.useForm<{ title: string; operationId: string }>()
  const [error, setError] = useState<string | null>(null)
  const live = useRef(true)
  const locked = useRef(disabled)
  const validating = useRef(false)
  locked.current = disabled
  useEffect(() => { live.current = true; return () => { live.current = false } }, [])
  const initial = useMemo(() => ({ title: draft.step?.title, operationId: draft.step?.operation_id }), [draft])
  const options = useMemo(() => {
    const groups = new Map<string, { value: string; label: string }[]>()
    operations.forEach((item) => {
      const label = item.skill_label ?? '业务能力'
      const items = groups.get(label) ?? []
      items.push({ value: item.operation_id, label: `${item.label}${item.enabled === false ? '（未启用）' : ''}` })
      groups.set(label, items)
    })
    return [...groups].map(([label, items]) => ({ label, options: items }))
  }, [operations])
  const operationId = Form.useWatch('operationId', form)
  const operation = operations.find((item) => item.operation_id === operationId)
  const suggestedName = useRef<string | undefined>(undefined)
  const chooseOperation = useCallback((id: string) => {
    const selected = operations.find((item) => item.operation_id === id)
    if (!selected) return
    const title = form.getFieldValue('title')
    if (!title || title === suggestedName.current) form.setFieldValue('title', selected.label)
    suggestedName.current = selected.label
  }, [form, operations])
  const details = useMemo(() => operation ? [
    { key: 'input', label: '使用信息', children: operation.input_fields.join('、') },
    { key: 'output', label: '处理结果', children: operation.output_fields.join('、') },
  ] : [], [operation])
  const submit = useCallback(async () => {
    if (validating.current) return
    validating.current = true
    try {
      const values = await form.validateFields()
      if (!live.current || locked.current) return
      const result = onSubmit({ kind: draft.kind, id: draft.id, title: values.title.trim(), operationId: values.operationId })
      if (result.kind === 'rejected') { setError(result.message); return }
      onClose()
    } catch (issue) { if (live.current && issue instanceof Error) setError(issue.message) }
    finally { validating.current = false }
  }, [draft, form, onClose, onSubmit])
  const confirm = useCallback(() => { void submit() }, [submit])
  return <Modal title={draft.kind === 'add' ? '新增步骤' : '编辑步骤'} open onCancel={onClose} onOk={confirm}
    okText={draft.kind === 'add' ? '添加节点' : '确定'} okButtonProps={OK_PROPS} cancelText="取消" confirmLoading={disabled} width={620}>
    {error ? <Alert type="error" title={error} showIcon role="alert" /> : null}
    {!operations.length ? <Alert type="warning" title="当前角色没有可用的业务操作，请先检查技能配置" showIcon /> : null}
    <Form form={form} layout="vertical" initialValues={initial} disabled={disabled} preserve={false}>
      <Form.Item name="operationId" label="业务操作" rules={OPERATION_RULES}>
        <Select options={options} showSearch optionFilterProp="label" onChange={chooseOperation} placeholder="搜索并选择 Skill 或能力" />
      </Form.Item>
      <Form.Item name="title" label="步骤名称" rules={NAME_RULES}>
        <Input maxLength={200} placeholder="选择能力后自动填写，可修改" />
      </Form.Item>
      {operation?.enabled === false ? <Alert type="warning" title={operation.unavailable_reason || '此能力尚未启用，当前可设计和保存草稿'} showIcon /> : null}
      {operation ? <><Typography.Paragraph type="secondary">{operation.description}</Typography.Paragraph>
        <Descriptions column={1} size="small" items={details} /></> : null}
    </Form>
  </Modal>
}
